import React from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

/**
 * Feature-level soft paywall. Wrap ANY Pro-only content.
 *  - Pro users → renders `children`
 *  - Free / trial-expired users → renders a compact upsell card in place of children
 *
 * Usage:
 *   <UpgradeGate feature="Practice analytics" description="See patient trends and repeat visits.">
 *     <AnalyticsChart data={...} />
 *   </UpgradeGate>
 */
export default function UpgradeGate({ feature, description, children, ctaLabel, testid, minimal = false }) {
  const { is_pro, loading, eligible_plan } = useSubscription();
  if (loading) return <div className="h-24 rounded-md bg-muted/60 animate-pulse" />;
  if (is_pro) return children;

  if (minimal) {
    return (
      <Link
        to="/upgrade"
        className="inline-flex items-center gap-1.5 text-xs rounded-full bg-primary/10 text-primary px-2.5 py-1 hover:bg-primary/15"
        data-testid={testid || "upgrade-gate-minimal"}
      >
        <Lock size={11} /> Pro — Upgrade
      </Link>
    );
  }

  return (
    <div
      className="rounded-lg border border-dashed border-primary/30 bg-primary/[0.03] p-6 md:p-8"
      data-testid={testid || "upgrade-gate"}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Sparkles size={18} />
        </div>
        <div className="min-w-0">
          <div className="overline text-primary/70">{eligible_plan?.name || "Pro"} feature</div>
          <h3 className="mt-1 font-serif text-2xl text-primary">{feature}</h3>
          {description && <p className="mt-1 text-sm text-muted-foreground max-w-lg">{description}</p>}
          <Link
            to="/upgrade"
            className="mt-4 inline-flex items-center rounded-md bg-primary text-primary-foreground text-sm font-medium px-4 py-2"
            data-testid="upgrade-gate-cta"
          >
            {ctaLabel || "Upgrade to unlock"}
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Small "Pro" tag chip. */
export function ProChip({ testid }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5"
      data-testid={testid || "pro-chip"}
    >
      <Sparkles size={9} /> Pro
    </span>
  );
}
